// StretchTriad — current / longest / average row (PR 1B, Gap 2).
//
// The community on r/quittingkratom has actively chosen calendar-anchored
// quit-dates over single-streak counters. This triad keeps a streak
// visible while reframing it as "you've done this before — here's the
// pattern": current, longest, average.
//
// Mobile discipline:
//  - One row of three stats. Each cell ≤120px wide so the triad fits
//    375px viewport without wrapping. Stats use the tabular-numeric
//    text style for clean alignment.
//  - Tone: when longest > current (i.e. the user is rebuilding after a
//    lapse), the helper line below switches to a quiet "your record
//    stands" message. Never shouts the gap.
//  - No animation; renders alongside the existing rings inside the
//    "How today's going" expander.

import type { StreakInfo } from '@shared/types';

interface StretchTriadProps {
  streak: StreakInfo | null;
}

export function StretchTriad({ streak }: StretchTriadProps) {
  if (!streak) return null;
  const { currentStreakDays, longestStreakDays, averageStretchDays } = streak;

  // No data yet: hide the row. A first-week user will see ConsistencyRing
  // celebrate the early streak; the triad is for after they've completed
  // at least one stretch.
  if (longestStreakDays === 0 && currentStreakDays === 0 && averageStretchDays === 0) {
    return null;
  }

  // Lapse-aware copy: when the user has a recorded record higher than
  // the current run, anchor on the record instead of the gap. Otherwise
  // surface a neutral "since you started tracking" framing.
  const showingRebuild = longestStreakDays > currentStreakDays && longestStreakDays > 0;
  const helperCopy = showingRebuild
    ? "Your record stands — you've done it before."
    : 'A slip resets the current streak. The record and average don\'t.';

  return (
    <div className="stretch-triad" role="group" aria-label="streak history">
      <StretchCell label="Current" value={currentStreakDays} suffix={dayLabel(currentStreakDays)} />
      <StretchCell label="Longest" value={longestStreakDays} suffix={dayLabel(longestStreakDays)} />
      <StretchCell
        label="Average"
        value={averageStretchDays === 0 ? '—' : averageStretchDays.toFixed(1)}
        suffix={averageStretchDays === 0 ? '' : dayLabel(averageStretchDays)}
      />
      <p className="stretch-triad-helper dim text-caption">{helperCopy}</p>
    </div>
  );
}

function dayLabel(n: number): string {
  return n === 1 ? 'day' : 'days';
}

function StretchCell({
  label,
  value,
  suffix,
}: {
  label: string;
  value: number | string;
  suffix: string;
}) {
  return (
    <div className="stretch-cell">
      <div className="stretch-cell-label">{label}</div>
      <div className="stretch-cell-value">
        <span className="stretch-cell-number">{value}</span>
        {suffix ? <span className="stretch-cell-suffix"> {suffix}</span> : null}
      </div>
    </div>
  );
}
